/* eslint-disable max-lines */

import { BREACHES_TYPES } from '@/lib/constants/easm/breaches'

export const EASM_DASHBOARD = {
  TITLE: 'EASM Dashboard',
  SUBTITLE: 'External attack surface management',
  SUMMARY: {
    PUBLIC_DOMAINS: 'Public Domains',
    DOMAINS: 'Domains',
    SUBDOMAIN_SINGULAR: 'subdomain',
    SUBDOMAIN_PLURAL: 'subdomains',
    EXPOSED_IPS: 'Exposed IPs',
    VULNERABILITY_SINGULAR: 'vulnerabilities',
    BREACH_PLURAL: 'breaches',
    CERTIFICATE_PLURAL: 'certificates',
    EXPOSURE_BREAKDOWN: 'Exposure Breakdown',
    NO_DATA: 'No data',
  },
  SEVERITY_CARD: {
    TITLE: 'Vulnerability Distribution',
    NO_VULNERABILITIES: 'No vulnerabilities detected',
  },
  TOP_DOMAINS: {
    TITLE: 'Top Risk Subdomains',
    NO_DATA: 'No data available',
    RISK_SCORE: 'Risk Score',
  },
  BREACHES: {
    TITLE: 'Known Breaches',
    TOTAL: 'Total',
    CRITICAL: 'Critical',
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
    UNKNOWN: 'Unknown',
    PASSWORD_EXPOSED: 'Password exposed',
    NON_CRITICAL: 'non critical',
    NO_BREACHES: 'No breaches detected',
  },
  CERTIFICATES: {
    TITLE: 'Certificates',
    TOTAL: 'Total',
    VALID: 'Valid',
    EXPIRED: 'Expired',
    NO_CERTIFICATES: 'No certificates found',
  },
  TRENDS: {
    TITLE: 'Vulnerability Trends',
    COMPUTING: 'Computing trends',
    LAST_SCAN: 'Last scan',
  },
  DOWNLOAD_REPORT: 'Download Report',
}

export const EASM_PUBLIC_DOMAINS = {
  TITLE: 'Public Domains',
  SEARCH_PLACEHOLDER: 'Search subdomains...',
  NO_PUBLIC_DOMAINS_FOUND: 'No public domains found',
  MONITORING: {
    LABEL: 'Monitoring',
    DOMAIN_SINGULAR: 'domain',
    DOMAIN_PLURAL: 'domains',
    SUBDOMAIN_SINGULAR: 'subdomain',
    SUBDOMAIN_PLURAL: 'subdomains',
  },
  PUBLIC_DOMAIN_CARD: {
    SUBDOMAIN: 'Subdomain',
    DOMAIN: 'Domain',
    IP_ADDRESS: 'IP Address',
    IP_ADDRESSES: 'IP Addresses',
    TECHNOLOGIES: 'Technologies',
    PORTS: 'Ports',
  },
  PORT_STATUS: {
    open: 'Open',
    closed: 'Closed',
    filtered: 'Filtered',
  },
}
export const EASM_CERTIFICATES = {
  TITLE: 'Certificates',
  NO_CERTIFICATES_FOUND: 'No certificates found',
  SEARCH_PLACEHOLDER: 'Search certificates...',
  COLUMNS: {
    STATUS: 'Status',
    NAME: 'Name',
    DOMAIN: 'Domain',
    ALGORITHM: 'Algorithm',
    KEY_SIZE: 'Key Size',
    SIGNATURE: 'Signature',
  },
  MONITORING: {
    LABEL: 'Monitoring',
    CERTIFICATE_SINGULAR: 'certificate',
    CERTIFICATE_PLURAL: 'certificates',
    DOMAIN_SINGULAR: 'domain',
    DOMAIN_PLURAL: 'domains',
    VALID_SINGULAR: 'valid',
    VALID_PLURAL: 'valid',
    EXPIRED_SINGULAR: 'expired',
    EXPIRED_PLURAL: 'expired',
  },
  CERTIFICATE_CARD: {
    DOMAINS: 'Assigned Domains',
    STATUS: 'Status',
    ALGORITHM: 'Algorithm:',
    KEY_SIZE: 'Key Size:',
    SIGNATURE_ALGORITHM: 'Signature:',
    DETAILS: 'Certificate Details',
    ENDPOINTS: 'Endpoints',
    ENDPOINT_IP: 'IP',
    ENDPOINT_HOSTNAME: 'Subdomain',
    ENDPOINT_PORT: 'Port',
    SAN: 'Subject Alternative Names',
    ISSUER: 'Issuer:',
    VALID_FROM: 'Valid From:',
    VALID_TO: 'Valid To:',
    TECHNICAL_DETAILS: 'Technical Details',
    SERIAL_NUMBER: 'Serial Number',
    SHA1_FINGERPRINT: 'SHA1 Fingerprint',
    SHA256_FINGERPRINT: 'SHA256 Fingerprint',
  },
  CERTIFICATE_STATUS: {
    valid: 'Valid',
    expired: 'Expired',
  },
}

export const EASM_VULNERABILITIES = {
  DESCRIPTION: 'Description',
  TITLE: 'External Vulnerabilities',
  OCCURRENCES: 'Occurrences',
  AFFECTED_ASSETS: 'Affected Hosts',
  DETAILS: 'Details',
  REMEDIATION: 'Remediation',
  CVSS_SCORE: 'CVSS Score',
  CVE_ID: 'CVE',
  PORT: 'Port',
  TOTAL_VULNERABILITIES: 'Total',
  CRITICALITY: {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  },
  STATUS: {
    all: 'All',
    open: 'Open',
    closed: 'Closed',
    muted: 'Muted',
  },
  // A column label, unlike STATUS above, which holds the tab values.
  TABLE: {
    STATUS: 'Status',
  },
  SEARCH_PLACEHOLDER: 'Search vulnerabilities...',
  EMPTY_STATE: 'No vulnerabilities found',
  DRAWER: {
    SUMMARY: 'Summary',
    SEVERITY: 'Severity',
    CVSS: 'CVSS',
    HOSTS: 'Hosts',
    HOST: 'Host',
    PORT: 'Port',
    DESCRIPTION: 'Description',
    REMEDIATION: 'Remediation',
    AFFECTED_ASSETS: 'Affected Hosts',
  },
  ITEM: {
    NA: 'N/A',
    HOSTS: 'hosts',
    MORE: 'more',
  },
  MONITORING: {
    LABEL: 'Summary',
    VULNERABILITIES: 'vulnerabilities',
    VULNERABILITY: 'vulnerability',
    AFFECTED_HOSTS: 'affected hosts',
    AFFECTED_HOST: 'affected host',
    PORTS: 'ports',
    PORT: 'port',
  },
  // Mirrors APPSEC.MUTE key for key, kept separate so EASM can name its own scanner and host noun.
  MUTE: {
    MUTE_BUTTON: 'Mute finding',
    UNMUTE_BUTTON: 'Unmute finding',
    DIALOG_TITLE: 'Reason for mute',
    SELECTION_SUMMARY: (selected: number, total: number) => `${selected} of ${total} occurrences selected`,
    JUSTIFICATION_LABEL: 'Justification',
    JUSTIFICATION_PLACEHOLDER:
      'Why is this finding being muted? e.g. a false positive from an external scan, accepted risk until the host is patched',
    CANCEL: 'Cancel',
    BANNER_TITLE: (muted: number, total: number) => `Muted · ${muted} of ${total} occurrences`,
    MUTED_BADGE: 'Muted',
    MUTED_BY: (name: string) => `Muted by ${name}`,
    BY_SCANNER: 'Muted by the external scanner',
    NOTHING_SELECTED_TOOLTIP: 'Select at least one occurrence',
    DORMANT_NOTE: 'This mute will apply again if the finding is reported in a future scan',
    MUTE_SUCCESS: 'Finding muted',
    UNMUTE_SUCCESS: 'Finding unmuted',
    MUTE_PARTIAL: (muted: number, requested: number) =>
      `Muted ${muted} of ${requested} occurrences — the rest are no longer open`,
    UNMUTE_PARTIAL: (unmuted: number, requested: number) =>
      `Unmuted ${unmuted} of ${requested} occurrences — the rest were no longer muted`,
    MUTE_ERROR: 'Could not mute the finding',
    UNMUTE_ERROR: 'Could not unmute the finding',
  },
}

export const EASM_BREACHES = {
  TITLE: 'Known Breaches',
  NO_BREACHES_FOUND: 'No breaches found',
  SEARCH_PLACEHOLDER: 'Search breaches...',
  COLUMNS: {
    SEVERITY: 'Severity',
    NAME: 'Name',
    DOMAIN: 'Domain',
    TYPE: 'Type',
    DATABASE: 'Database',
    IP_TRACKED: 'IP Tracked',
    REVIEWED: 'Reviewed',
    ACTIONS: 'Actions',
  },
  MONITORING: {
    LABEL: 'Monitoring',
    BREACH_SINGULAR: 'breach',
    BREACH_PLURAL: 'breaches',
    DOMAIN_SINGULAR: 'domain',
    DOMAIN_PLURAL: 'domains',
    CRITICAL_SINGULAR: 'critical',
    CRITICAL_PLURAL: 'critical',
    HIGH_SINGULAR: 'high',
    HIGH_PLURAL: 'high',
    REVIEWED_SINGULAR: 'reviewed',
    REVIEWED_PLURAL: 'reviewed',
  },
  REVIEW: {
    REVIEW_BUTTON: 'Review',
    MUTE_BUTTON: 'Mark as reviewed',
    DIALOG_TITLE: 'Mark as reviewed',
    JUSTIFICATION_LABEL: 'Justification (optional)',
    JUSTIFICATION_PLACEHOLDER: 'e.g. password rotated, user notified',
    CANCEL: 'Cancel',
    MUTE_SUCCESS: 'Breach marked as reviewed',
    MUTE_PARTIAL: (reviewed: number, requested: number) => `Marked ${reviewed} of ${requested} breaches as reviewed`,
    MUTE_ERROR: 'Could not mark the breach as reviewed',
    REVIEWED: 'Reviewed',
    NOT_REVIEWED: 'Not reviewed',
    REVIEWED_BY: (name: string) => `Reviewed by ${name}`,
  },
  REVIEW_FILTER: {
    ALL: 'All',
    PENDING: 'Not reviewed',
    REVIEWED: 'Reviewed',
  },
  EXPORT: {
    BUTTON: 'Export CSV',
    EMPTY: 'There are no breaches to export.',
    REVIEWED_REASON: 'Review justification',
    REVIEWED_BY: 'Reviewed by',
    REVIEWED_WHEN: 'Reviewed on',
  },
  TYPES_INFO: {
    [BREACHES_TYPES.PASSWORD]: 'Password found in plain text.', // TODO: Add tooltip info
    [BREACHES_TYPES.HASHED]: 'Password found, but hashed.', // TODO: Add tooltip info
    [BREACHES_TYPES.NOT_AVAILABLE]: 'No password found, but email or other data exposed.', // TODO: Add tooltip info
  },
  IP_TRACKED_INFO: 'User IP address involved in the breach (e.g. login or request)', // TODO: Add tooltip info
  BREACH_CARD: {
    DOMAIN: 'Domain',
    NAME: 'Name',
    TYPE: 'Type',
    SEVERITY: 'Severity',
    DATABASE: 'Database',
    IP_TRACKED: 'IP Tracked',
    IP_TRACKED_YES: 'Yes',
    IP_TRACKED_NO: 'No',
    TYPES: {
      [BREACHES_TYPES.PASSWORD]: 'Password',
      [BREACHES_TYPES.HASHED]: 'Hashed',
      [BREACHES_TYPES.NOT_AVAILABLE]: 'Unknown',
    },
    SEVERITIES: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      unknown: 'Unknown',
    },
  },
}
